import { getActiveUserAndPermission, type ActiveAppUser } from './userAdministration';
import { getStorage, removeStorage, setStorage } from '../utils/authStorage';
import { destroyRbacSession } from '../rbac/permissionService';

export type { ActiveAppUser };

/** CU-AUC-5 — active app user cached at login (`agd.appUser`). */
export function getStoredActiveAppUser(): ActiveAppUser | null {
  return getStorage<ActiveAppUser>('appUser');
}

export function clearStoredActiveAppUser(): void {
  removeStorage('appUser');
  clearAppUserInactiveFlag();
  // Clear any session-scoped RBAC snapshot linked to this user.
  destroyRbacSession();
}

export function markAppUserInactive(): void {
  setStorage('appUserInactive', true);
}

export function clearAppUserInactiveFlag(): void {
  removeStorage('appUserInactive');
}

export function isAppUserInactive(): boolean {
  return getStorage<boolean>('appUserInactive') === true;
}

export function persistActiveAppUser(user: ActiveAppUser): void {
  setStorage('appUser', user);
}

export function getAppUserId(): string | null {
  const id = getStoredActiveAppUser()?.id;
  return typeof id === 'string' && id.trim() ? id.trim() : null;
}

export function getActiveAppUserDisplayName(): string {
  return getStoredActiveAppUser()?.displayName?.trim() ?? '';
}

export function getActiveAppUserRole(): string {
  const u = getStoredActiveAppUser();
  return u?.defaultRole?.trim() || u?.roleDescription?.trim() || '';
}

/**
 * CU-AUC-5 — fetch GET /v1/user/app-user/active and persist for role-aware UI.
 * Cleared on logout via clearStoredActiveAppUser().
 */
export type SyncActiveAppUserResult =
  | { status: 'ok'; data: ActiveAppUser }
  | { status: 'inactive' }
  | { status: 'failed'; message: string };

export async function syncActiveAppUserOnLogin(): Promise<SyncActiveAppUserResult> {
  const res = await getActiveUserAndPermission();
  if (!res.success) {
    clearStoredActiveAppUser();
    return { status: 'failed', message: res.message };
  }
  if (!res.data.active) {
    clearStoredActiveAppUser();
    markAppUserInactive();
    return { status: 'inactive' };
  }
  clearAppUserInactiveFlag();
  persistActiveAppUser(res.data);
  return { status: 'ok', data: res.data };
}
